<template>
  <OnboardingLayout
    :title="$t('onboarding.features.title')"
    :subtitle="$t('onboarding.features.subtitle')"
    :can-proceed="canProceed"
    :progress-bar1="100"
    :progress-bar2="100"
    :skip-button-label="$t('onboarding.common.back')"
    @next="handleNext"
    @skip="handleSkip"
  >
    <div class="onboarding-features-grid">
      <FeatureCard
        v-for="feature in availableFeatures"
        :key="feature.id"
        :feature="feature"
        :selected="isSelected(feature.id)"
        @click="toggleFeature(feature.id)"
      />
    </div>
  </OnboardingLayout>
</template>

<script>
import { mapMutations, mapState } from 'vuex';
import OnboardingLayout from '../OnboardingLayout.vue';
import FeatureCard from '../shared/FeatureCard.vue';

export default {
  name: 'StepFeatures',

  components: {
    OnboardingLayout,
    FeatureCard
  },

  data() {
    return {
      localSelectedFeatures: []
    }
  },

  computed: {
    ...mapState('onboarding', ['selectedFeatures', 'selectedNetworks']),

    // Map features to networks that support them
    featureNetworkMap() {
      return {
        // Universal features
        planning: ['facebook', 'instagram', 'linkedin', 'twitter', 'google', 'threads', 'tiktok', 'youtube', 'bluesky', 'pinterest'],
        reporting: ['facebook', 'instagram', 'linkedin', 'twitter', 'google', 'tiktok', 'youtube', 'bluesky', 'pinterest'],
        moderation: ['facebook', 'instagram', 'linkedin', 'google', 'tiktok', 'youtube', 'bluesky'],
        collaboration: ['facebook', 'instagram', 'linkedin', 'twitter', 'google', 'threads', 'tiktok', 'youtube', 'bluesky', 'pinterest'],
        
        // Native connection features (one per network)
        'facebook.native': ['facebook'],
        'instagram.native': ['instagram'],
        'linkedin.native': ['linkedin'],
        'tiktok.native': ['tiktok']
      }
    },

    // Check if at least one selected network supports the feature
    isFeatureAvailable() {
      return (featureId) => {
        const supportedNetworks = this.featureNetworkMap[featureId] || []
        return this.selectedNetworks.some(network => supportedNetworks.includes(network))
      }
    },

    availableFeatures() {
      const allFeatures = [
        // Universal features
        {
          id: 'planning',
          title: this.$t('onboarding.features.planning.title'),
          description: this.$t('onboarding.features.planning.description')
        },
        {
          id: 'reporting',
          title: this.$t('onboarding.features.reporting.title'),
          description: this.$t('onboarding.features.reporting.description')
        },
        {
          id: 'moderation',
          title: this.$t('onboarding.features.moderation.title'),
          description: this.$t('onboarding.features.moderation.description')
        },
        {
          id: 'collaboration',
          title: this.$t('onboarding.features.collaboration.title'),
          description: this.$t('onboarding.features.collaboration.description')
        },
        
        // Native features (one card per network)
        {
          id: 'facebook.native',
          network: 'facebook',
          title: this.$t('onboarding.features.facebook.native.title'),
          description: this.$t('onboarding.features.facebook.native.description'),
          badges: [
            {
              label: this.$t('onboarding.features.facebook.native_post.title'),
              description: this.$t('onboarding.features.facebook.native_post.description')
            },
            {
              label: this.$t('onboarding.features.facebook.groups.title'),
              description: this.$t('onboarding.features.facebook.groups.description')
            },
            {
              label: this.$t('onboarding.features.facebook.personal.title'),
              description: this.$t('onboarding.features.facebook.personal.description')
            },
          ]
        },
        {
          id: 'instagram.native',
          network: 'instagram',
          title: this.$t('onboarding.features.instagram.native.title'),
          description: this.$t('onboarding.features.instagram.native.description'),
          badges: [
            {
              label: this.$t('onboarding.features.instagram.native_post.title'),
              description: this.$t('onboarding.features.instagram.native_post.description')
            },
            {
              label: this.$t('onboarding.features.instagram.stories.title'),
              description: this.$t('onboarding.features.instagram.stories.description')
            },
            {
              label: this.$t('onboarding.features.instagram.mention.title'),
              description: this.$t('onboarding.features.instagram.mention.description')
            },
            {
              label: this.$t('onboarding.features.instagram.music.title'),
              description: this.$t('onboarding.features.instagram.music.description')
            },
            {
              label: this.$t('onboarding.features.instagram.hashtag.title'),
              description: this.$t('onboarding.features.instagram.hashtag.description')
            },
            {
              label: this.$t('onboarding.features.instagram.sticker.title'),
              description: this.$t('onboarding.features.instagram.sticker.description')
            },
          ]
        },
        {
          id: 'linkedin.native',
          network: 'linkedin',
          title: this.$t('onboarding.features.linkedin.native.title'),
          description: this.$t('onboarding.features.linkedin.native.description'),
          badges: [
            {
              label: this.$t('onboarding.features.linkedin.native_post.title'),
              description: this.$t('onboarding.features.linkedin.native_post.description')
            },
            {
              label: this.$t('onboarding.features.linkedin.mentions.title'),
              description: this.$t('onboarding.features.linkedin.mentions.description')
            },
            {
              label: this.$t('onboarding.features.linkedin.groups.title'),
              description: this.$t('onboarding.features.linkedin.groups.description')
            },
            {
              label: this.$t('onboarding.features.linkedin.carousel.title'),
              description: this.$t('onboarding.features.linkedin.carousel.description')
            },
            {
              label: this.$t('onboarding.features.linkedin.poll.title'),
              description: this.$t('onboarding.features.linkedin.poll.description')
            },
            {
              label: this.$t('onboarding.features.linkedin.inbox.title'),
              description: this.$t('onboarding.features.linkedin.inbox.description')
            }
          ]
        },
        {
          id: 'tiktok.native',
          network: 'tiktok',
          title: this.$t('onboarding.features.tiktok.native.title'),
          description: this.$t('onboarding.features.tiktok.native.description'),
          badges: [
            {
              label: this.$t('onboarding.features.tiktok.mentions.title'),
              description: this.$t('onboarding.features.tiktok.mentions.description')
            },
            {
              label: this.$t('onboarding.features.tiktok.reporting.title'),
              description: this.$t('onboarding.features.tiktok.reporting.description')
            },
            {
              label: this.$t('onboarding.features.tiktok.music.title'),
              description: this.$t('onboarding.features.tiktok.music.description')
            },
            // {
            //   label: this.$t('onboarding.features.tiktok.stories.title'),
            //   description: this.$t('onboarding.features.tiktok.stories.description')
            // },
            {
              label: this.$t('onboarding.features.tiktok.video.title'),
              description: this.$t('onboarding.features.tiktok.video.description')
            }
          ]
        }
      ]

      // Filter features based on selected networks
      const filtered = allFeatures.filter(feature => this.isFeatureAvailable(feature.id))
      
      console.log('[StepFeatures] Selected networks:', this.selectedNetworks)
      console.log('[StepFeatures] Filtered features:', filtered.map(f => f.id))
      
      return filtered
    },

    canProceed() {
      return this.localSelectedFeatures.length > 0
    }
  },

  mounted() {
    console.log('[StepFeatures] Component mounted')
    console.log('[StepFeatures] Selected networks from store:', this.selectedNetworks)
    console.log('[StepFeatures] Available features after filtering:', this.availableFeatures.map(f => f.id))
    
    // Redirect to networks step if no networks selected
    if (!this.selectedNetworks || this.selectedNetworks.length === 0) {
      console.warn('[StepFeatures] No networks selected - redirecting to networks step')
      this.$router.push({ name: 'onboarding-networks' })
      return
    }
    
    // Restore state from store
    this.localSelectedFeatures = [
      ...this.$store.state.onboarding.selectedFeatures
    ]
    
    // Clean up features that are no longer available (network was deselected)
    this.localSelectedFeatures = this.localSelectedFeatures.filter(featureId => 
      this.availableFeatures.some(f => f.id === featureId)
    )
    
    console.log('[StepFeatures] Restored selected features:', this.localSelectedFeatures)
  },

  methods: {
    ...mapMutations('onboarding', ['SET_FEATURES', 'SET_STEP']),

    isSelected(featureId) {
      return this.localSelectedFeatures.includes(featureId)
    },

    toggleFeature(featureId) {
      const index = this.localSelectedFeatures.indexOf(featureId)
      
      if (index > -1) {
        // Deselecting a feature
        this.localSelectedFeatures.splice(index, 1)
        console.log('[StepFeatures] Feature deselected:', featureId)
      } else {
        // Selecting a feature
        this.localSelectedFeatures.push(featureId)
        console.log('[StepFeatures] Feature selected:', featureId)
      }
      
      console.log('[StepFeatures] Current selected features:', [...this.localSelectedFeatures])
    },

    handleNext() {
      if (!this.canProceed) {
        console.log('[StepFeatures] Cannot proceed - no features selected')
        return
      }

      console.log('[StepFeatures] handleNext - Selected features:', this.localSelectedFeatures)
      // Save to store
      this.SET_FEATURES(this.localSelectedFeatures)
      this.SET_STEP(3)
      console.log('[StepFeatures] Store updated:', this.$store.state.onboarding.selectedFeatures)

      // Navigate to next step
      console.log('[StepFeatures] Navigating to onboarding-connectPages')
      this.$router.push({ name: 'onboarding-connectPages' })
    },

    handleSkip() {
      console.log('[StepFeatures] User clicked back - returning to networks step')
      // Save current feature selections to store before going back
      this.SET_FEATURES(this.localSelectedFeatures)
      // Navigate back to networks step (no cleanup, user is still in onboarding)
      this.$router.push({ name: 'onboarding-networks' })
    }
  }
}
</script>

<style scoped lang="scss">
.onboarding-features-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 16px;
  max-width: 100%;
  
  > * {
    min-width: 0; // Allow grid items to shrink below content size
  }
}

@media (max-width: 1024px) {
  .onboarding-features-grid {
    grid-template-columns: repeat(2, 1fr);
  }
}

@media (max-width: 768px) {
  .onboarding-features-grid {
    grid-template-columns: 1fr;
    gap: 12px;
  }
}
</style>
